import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/cookie")({
  head: () => ({
    meta: [
      { title: "Cookie Policy — ChefConnect Italia" },
      {
        name: "description",
        content:
          "Quali cookie utilizza ChefConnect Italia, a cosa servono e come gestire le tue preferenze.",
      },
      { property: "og:title", content: "Cookie Policy — ChefConnect Italia" },
      {
        property: "og:description",
        content:
          "Cookie tecnici, di preferenza e di analisi: cosa usiamo e come gestirli.",
      },
      { property: "og:url", content: "https://chefconnectitalia.com/cookie" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/cookie" }],
  }),
  component: CookiePage,
});

function CookiePage() {
  return (
    <article className="container mx-auto max-w-3xl px-4 py-16 sm:py-24">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Legale</p>
      <h1 className="mt-3 font-display text-5xl">Cookie Policy</h1>
      <p className="mt-3 text-sm text-muted-foreground">Ultimo aggiornamento: 27 giugno 2026</p>

      <div className="mt-10 space-y-10 text-[15px] leading-relaxed text-muted-foreground">
        <p>
          Questa Cookie Policy descrive i cookie e le tecnologie simili
          utilizzati su{" "}
          <a href="https://chefconnectitalia.com" className="text-foreground hover:text-gold">
            chefconnectitalia.com
          </a>{" "}
          ("ChefConnect"). Costituisce parte integrante della{" "}
          <Link to="/privacy" className="text-foreground hover:text-gold">
            Privacy Policy
          </Link>
          .
        </p>

        <Section title="1. Cosa sono i cookie">
          <p>
            I cookie sono piccoli file di testo memorizzati dal browser durante
            la navigazione. Servono a far funzionare il sito, ricordare le tue
            preferenze e, in alcuni casi, analizzare in forma aggregata l'uso
            della piattaforma.
          </p>
        </Section>

        <Section title="2. Tipologie di cookie utilizzati">
          <h3 className="mt-2 font-medium text-foreground">Cookie tecnici (necessari)</h3>
          <p className="mt-2">
            Indispensabili al funzionamento del sito: autenticazione, sessione
            utente, sicurezza, mantenimento delle preferenze di tema (chiaro /
            scuro). Non richiedono consenso.
          </p>
          <h3 className="mt-6 font-medium text-foreground">Cookie di terze parti</h3>
          <p className="mt-2">
            Alcune funzionalità si appoggiano a servizi esterni che possono
            impostare cookie propri:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>
              <strong className="text-foreground">Lovable Cloud</strong> —
              autenticazione e sessione utente.
            </li>
            <li>
              <strong className="text-foreground">Stripe</strong> — checkout
              sicuro degli abbonamenti Premium e prevenzione frodi.
            </li>
            <li>
              <strong className="text-foreground">OpenStreetMap / Leaflet</strong> —
              caricamento delle tile della mappa interattiva.
            </li>
          </ul>
        </Section>

        <Section title="3. Gestione delle preferenze">
          <p>
            Puoi accettare, rifiutare o cancellare i cookie in qualsiasi momento
            modificando le impostazioni del tuo browser. La maggior parte dei
            browser permette di:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-5">
            <li>visualizzare i cookie presenti e cancellarli singolarmente;</li>
            <li>bloccare i cookie di terze parti;</li>
            <li>bloccare i cookie di siti specifici;</li>
            <li>bloccare l'impostazione di tutti i cookie;</li>
            <li>cancellare tutti i cookie alla chiusura del browser.</li>
          </ul>
          <p className="mt-4">
            Disabilitando i cookie tecnici alcune funzioni del sito (accesso,
            sessione, checkout) potrebbero non funzionare correttamente.
          </p>
        </Section>

        <Section title="4. Contatti">
          <p>
            Per domande sulla gestione dei cookie e dei dati personali scrivi a{" "}
            <a href="mailto:privacy@chefconnectitalia.com" className="text-foreground hover:text-gold">
              privacy@chefconnectitalia.com
            </a>
            .
          </p>
        </Section>
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl text-foreground">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}